import { useEffect, useState } from 'react';
import { fetchActiveAffectations, fetchPatientsWithoutAffectation, fetchPatientsList, fetchDoctorsList, assignAffectation } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export default function AffectationList() {
    const [affectations, setAffectations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [form, setForm] = useState({ patient_id: '', doctor_id: '', motif: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Popup state for patient/doctor selection
    const [showPatientPopup, setShowPatientPopup] = useState(false);
    const [showDoctorPopup, setShowDoctorPopup] = useState(false);
    const [patientSearch, setPatientSearch] = useState('');
    const [doctorSearch, setDoctorSearch] = useState('');

    const { token } = useAuth();
    const navigate = useNavigate();

    // Nouvel état pour les patients sans affectation
    const [patientsWithoutAffectation, setPatientsWithoutAffectation] = useState([]);
    const [loadingPatientsWithout, setLoadingPatientsWithout] = useState(true);
    // Pour popup d'affectation rapide
    const [showQuickAssign, setShowQuickAssign] = useState(false);
    const [quickAssignPatient, setQuickAssignPatient] = useState(null);
    const [quickAssignDoctors, setQuickAssignDoctors] = useState([]);
    const [quickAssignDoctorId, setQuickAssignDoctorId] = useState('');
    const [quickAssignMotif, setQuickAssignMotif] = useState('');
    const [quickAssignNotes, setQuickAssignNotes] = useState('');
    const [quickAssignLoading, setQuickAssignLoading] = useState(false);
    const [quickAssignError, setQuickAssignError] = useState('');

    // Ouvre la popup d'affectation rapide pour un patient
    const openQuickAssign = async (patient) => {
        setQuickAssignPatient(patient);
        setShowQuickAssign(true);
        setQuickAssignDoctorId('');
        setQuickAssignMotif('');
        setQuickAssignNotes('');
        setQuickAssignError('');
        setQuickAssignLoading(true);
        try {
            const doctors = await fetchDoctorsList();
            setQuickAssignDoctors(doctors);
        } catch (e) {
            setQuickAssignDoctors([]);
            setQuickAssignError('Erreur lors du chargement des médecins');
        }
        setQuickAssignLoading(false);
    };

    // Ferme la popup d'affectation rapide
    const closeQuickAssign = () => {
        setShowQuickAssign(false);
        setQuickAssignPatient(null);
        setQuickAssignDoctors([]);
        setQuickAssignDoctorId('');
        setQuickAssignMotif('');
        setQuickAssignNotes('');
        setQuickAssignError('');
        setQuickAssignLoading(false);
    };

    // Valide l'affectation rapide
    const handleQuickAssign = async (e) => {
        e.preventDefault();
        if (!quickAssignDoctorId) {
            setQuickAssignError('Veuillez choisir un médecin');
            return;
        }
        setQuickAssignLoading(true);
        setQuickAssignError('');
        try {
            await assignAffectation({
                patient_id: quickAssignPatient.id,
                doctor_id: quickAssignDoctorId,
                motif: quickAssignMotif,
                notes: quickAssignNotes,
                token
            });
            // Refresh lists
            setLoading(true);
            setLoadingPatientsWithout(true);
            const [affs, patientsNo] = await Promise.all([
                fetchActiveAffectations(),
                fetchPatientsWithoutAffectation()
            ]);
            setAffectations(affs);
            setPatientsWithoutAffectation(patientsNo);
            setShowQuickAssign(false);
        } catch (err) {
            setQuickAssignError("Erreur lors de l'affectation");
        }
        setQuickAssignLoading(false);
        setLoading(false);
        setLoadingPatientsWithout(false);
    };

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const data = await fetchActiveAffectations();
                setAffectations(data);
                // Charger aussi les patients sans affectation
                setLoadingPatientsWithout(true);
                const data2 = await fetchPatientsWithoutAffectation();
                setPatientsWithoutAffectation(data2);
                setLoadingPatientsWithout(false);
            } catch (err) {
                setError('Erreur lors du chargement des affectations');
                setLoadingPatientsWithout(false);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    const openModal = async () => {
        setShowModal(true);
        setForm({ patient_id: '', doctor_id: '', motif: '', notes: '' });
        setFormError('');
        // Charger patients et doctors dès l'ouverture du modal
        setPatients([]);
        setDoctors([]);
        try {
            const [patientsData, doctorsData] = await Promise.all([
                fetchPatientsList(),
                fetchDoctorsList()
            ]);
            setPatients(Array.isArray(patientsData) ? patientsData : []);
            setDoctors(Array.isArray(doctorsData) ? doctorsData : []);
        } catch (e) {
            setPatients([]);
            setDoctors([]);
            setFormError('Erreur lors du chargement des utilisateurs');
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setFormError('');
    };

    const handleChange = e => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSubmitting(true);
        setFormError('');
        try {
            await assignAffectation({ ...form, token });
            // Refresh affectations
            const data = await fetchActiveAffectations();
            setAffectations(data);
            // Refresh patients sans affectation
            setLoadingPatientsWithout(true);
            const data2 = await fetchPatientsWithoutAffectation();
            setPatientsWithoutAffectation(data2);
            setLoadingPatientsWithout(false);
            setShowModal(false);
        } catch (err) {
            setFormError('Erreur lors de l\'affectation');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ padding: 32 }}>
            <h2 style={{ color: '#1976d2', marginBottom: 32 }}>Affectations actives</h2>
            <button onClick={() => navigate('/admin')} style={{ marginBottom: 24, marginRight: 16, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontWeight: 600, cursor: 'pointer' }}>
                ← Retour au Dashboard
            </button>
            <button onClick={openModal} style={{ marginBottom: 24, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', fontSize: 16, cursor: 'pointer' }}>
                + Ajouter une affectation
            </button>
            {loading ? (
                <div>Chargement...</div>
            ) : error ? (
                <div style={{ color: 'red' }}>{error}</div>
            ) : (
                <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px #e0e0e0', padding: 24 }}>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 16 }}>
                        <thead>
                            <tr style={{ background: '#f5f6fa' }}>
                                <th style={{ padding: '12px 8px' }}>Patient</th>
                                <th style={{ padding: '12px 8px' }}>Médecin</th>
                                <th style={{ padding: '12px 8px' }}>Date d'affectation</th>
                                <th style={{ padding: '12px 8px' }}>Motif</th>
                                <th style={{ padding: '12px 8px' }}>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {affectations.map(a => (
                                <tr key={a.id}>
                                    <td>{a.patient_name || a.patient_id}</td>
                                    <td>{a.doctor_name || a.doctor_id}</td>
                                    <td>{a.date_affectation ? new Date(a.date_affectation).toLocaleString() : ''}</td>
                                    <td>{a.motif}</td>
                                    <td>{a.notes}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Liste des patients sans affectation active */}
            <div style={{ marginTop: 40 }}>
                <h3 style={{ color: '#d32f2f', marginBottom: 16 }}>Patients sans affectation active</h3>
                {loadingPatientsWithout ? (
                    <div>Chargement...</div>
                ) : (
                    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px #e0e0e0', padding: 24 }}>
                        {patientsWithoutAffectation.length === 0 ? (
                            <div style={{ color: '#888' }}>Aucun patient sans affectation</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 16 }}>
                                <thead>
                                    <tr style={{ background: '#f5f6fa' }}>
                                        <th style={{ padding: '12px 8px' }}>Nom</th>
                                        <th style={{ padding: '12px 8px' }}>Prénom</th>
                                        <th style={{ padding: '12px 8px' }}>Email</th>
                                        <th style={{ padding: '12px 8px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {patientsWithoutAffectation.map(p => (
                                        <tr key={p.id}>
                                            <td>{p.last_name}</td>
                                            <td>{p.first_name}</td>
                                            <td>{p.email}</td>
                                            <td>
                                                <button onClick={() => openQuickAssign(p)} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 15, cursor: 'pointer' }}>
                                                    Affecter
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {/* Popup d'affectation rapide */}
            {showQuickAssign && quickAssignPatient && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <form onSubmit={handleQuickAssign} style={{ background: '#fff', borderRadius: 10, padding: 32, minWidth: 340, boxShadow: '0 2px 16px #bbb', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <h3 style={{ margin: 0, color: '#1976d2' }}>Affecter {quickAssignPatient.first_name} {quickAssignPatient.last_name}</h3>
                        <label>Médecin</label>
                        <select value={quickAssignDoctorId} onChange={e => setQuickAssignDoctorId(e.target.value)} required style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc' }}>
                            <option value="">Sélectionner...</option>
                            {quickAssignDoctors.map(d => (
                                <option key={d.id} value={d.id}>{d.first_name} {d.last_name} ({d.email})</option>
                            ))}
                        </select>
                        <label>Motif
                            <input value={quickAssignMotif} onChange={e => setQuickAssignMotif(e.target.value)} required style={{ width: '100%', padding: 8, marginTop: 4 }} />
                        </label>
                        <label>Notes
                            <textarea value={quickAssignNotes} onChange={e => setQuickAssignNotes(e.target.value)} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                        </label>
                        {quickAssignError && <div style={{ color: 'red' }}>{quickAssignError}</div>}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button type="button" onClick={closeQuickAssign} style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 15 }}>Annuler</button>
                            <button type="submit" disabled={quickAssignLoading} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 15, opacity: quickAssignLoading ? 0.7 : 1 }}>Valider</button>
                        </div>
                    </form>
                </div>
            )}

            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 10, padding: 32, minWidth: 340, boxShadow: '0 2px 16px #bbb', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <h3 style={{ margin: 0, color: '#1976d2' }}>Nouvelle affectation</h3>
                        <label htmlFor="patient_id">Patient</label>
                        <button
                            type="button"
                            id="patient_id"
                            name="patient_id"
                            aria-haspopup="listbox"
                            aria-expanded={showPatientPopup}
                            onClick={() => setShowPatientPopup(true)}
                            style={{
                                width: '100%',
                                padding: 8,
                                marginTop: 4,
                                borderRadius: 6,
                                border: '1px solid #ccc',
                                background: '#f5f6fa',
                                textAlign: 'left',
                                color: form.patient_id ? '#1976d2' : '#333',
                                fontWeight: form.patient_id ? 600 : 400
                            }}
                        >
                            {form.patient_id ? (() => {
                                const p = patients.find(x => String(x.id) === String(form.patient_id));
                                return p ? `${p.first_name || ''} ${p.last_name || ''} (${p.email})` : 'Sélectionner...';
                            })() : 'Sélectionner...'}
                        </button>
                        <label htmlFor="doctor_id">Médecin</label>
                        <button
                            type="button"
                            id="doctor_id"
                            name="doctor_id"
                            aria-haspopup="listbox"
                            aria-expanded={showDoctorPopup}
                            onClick={() => setShowDoctorPopup(true)}
                            style={{
                                width: '100%',
                                padding: 8,
                                marginTop: 4,
                                borderRadius: 6,
                                border: '1px solid #ccc',
                                background: '#f5f6fa',
                                textAlign: 'left',
                                color: form.doctor_id ? '#1976d2' : '#333',
                                fontWeight: form.doctor_id ? 600 : 400
                            }}
                        >
                            {form.doctor_id ? (() => {
                                const d = doctors.find(x => String(x.id) === String(form.doctor_id));
                                return d ? `${d.first_name || ''} ${d.last_name || ''} (${d.email})` : 'Sélectionner...';
                            })() : 'Sélectionner...'}
                        </button>
                        <label>
                            Motif
                            <input name="motif" value={form.motif} onChange={handleChange} required style={{ width: '100%', padding: 8, marginTop: 4 }} />
                        </label>
                        <label>
                            Notes
                            <textarea name="notes" value={form.notes} onChange={handleChange} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                        </label>
                        {formError && <div style={{ color: 'red' }}>{formError}</div>}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button type="button" onClick={closeModal} style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 15 }}>Annuler</button>
                            <button type="submit" disabled={submitting} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 15, opacity: submitting ? 0.7 : 1 }}>Valider</button>
                        </div>
                    </form>
                    {/* Patient selection popup */}
                    {showPatientPopup && (
                        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
                            <div style={{ background: '#fff', borderRadius: 10, padding: 24, minWidth: 340, maxHeight: 400, overflowY: 'auto', boxShadow: '0 2px 16px #bbb', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <input id="patient_search" name="patient_search" placeholder="Rechercher..." value={patientSearch} onChange={e => setPatientSearch(e.target.value)} style={{ marginBottom: 8, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                                <div style={{ flex: 1, overflowY: 'auto' }} role="listbox" aria-labelledby="patient_id">
                                    {patients && patients.length > 0 ? patients.filter(p => `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(patientSearch.toLowerCase())).map(p => (
                                        <div key={p.id} style={{ padding: 8, cursor: 'pointer', borderBottom: '1px solid #eee' }} role="option" aria-selected={form.patient_id === String(p.id)} onClick={() => { setForm(f => ({ ...f, patient_id: p.id })); setShowPatientPopup(false); }}>
                                            {p.first_name} {p.last_name} ({p.email})
                                        </div>
                                    )) : <div style={{ color: '#888', padding: 8 }}>Aucun patient trouvé</div>}
                                </div>
                                <button onClick={() => setShowPatientPopup(false)} style={{ marginTop: 8, background: '#eee', color: '#333', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 15 }}>Fermer</button>
                            </div>
                        </div>
                    )}
                    {/* Doctor selection popup */}
                    {showDoctorPopup && (
                        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
                            <div style={{ background: '#fff', borderRadius: 10, padding: 24, minWidth: 340, maxHeight: 400, overflowY: 'auto', boxShadow: '0 2px 16px #bbb', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <input id="doctor_search" name="doctor_search" placeholder="Rechercher..." value={doctorSearch} onChange={e => setDoctorSearch(e.target.value)} style={{ marginBottom: 8, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                                <div style={{ flex: 1, overflowY: 'auto' }} role="listbox" aria-labelledby="doctor_id">
                                    {doctors && doctors.length > 0 ? doctors.filter(d => `${d.first_name} ${d.last_name} ${d.email}`.toLowerCase().includes(doctorSearch.toLowerCase())).map(d => (
                                        <div key={d.id} style={{ padding: 8, cursor: 'pointer', borderBottom: '1px solid #eee' }} role="option" aria-selected={form.doctor_id === String(d.id)} onClick={() => { setForm(f => ({ ...f, doctor_id: d.id })); setShowDoctorPopup(false); }}>
                                            {d.first_name} {d.last_name} ({d.email})
                                        </div>
                                    )) : <div style={{ color: '#888', padding: 8 }}>Aucun médecin trouvé</div>}
                                </div>
                                <button onClick={() => setShowDoctorPopup(false)} style={{ marginTop: 8, background: '#eee', color: '#333', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 15 }}>Fermer</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
